// Inline one-card-at-a-time review deck (WP-H rev 2). Renders on Overview when
// the teal banner expands — proposed discussion items first, then proposed
// actions. Item accept = patch reviewState accepted; action accept = raise/link
// Jira (no local-only accept). Keyboard: → accept, ← dismiss, e edit.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Card, Button, TextField, TextArea, SelectField, Pill, Badge, MetricCard, Grid,
} from "../../design/ui";
import {
  patchDiscussionItem, patchVisitAction,
  type DiscussionItem, type VisitAction, type ItemStatus, type ItemDisposition, type RaiseTarget,
} from "../../lib/brain-api";
import { RaiseTicketDialog, type RaiseSubject } from "./RaiseTicketDialog";

const STATUS_OPTIONS: { value: ItemStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "investigate", label: "Investigate" },
  { value: "with-client", label: "With client" },
  { value: "needs-discussion", label: "Needs Discussion" },
  { value: "in-ticket", label: "In ticket" },
  { value: "done", label: "Done" },
  { value: "dropped", label: "Dropped" },
];

const TICKET_OPTIONS: { value: ItemDisposition; label: string }[] = [
  { value: "discuss", label: "—" },
  { value: "raise", label: "Raise" },
  { value: "raise-fr", label: "Raise FR" },
  { value: "road-map-fr", label: "Road map FR" },
];

const RAISE_DEFAULT: Record<string, RaiseTarget> = {
  raise: "support",
  "raise-fr": "feature-request",
  "road-map-fr": "feature-request",
  discuss: "support",
};

type QueueCard =
  | { kind: "item"; item: DiscussionItem }
  | { kind: "action"; action: VisitAction };

const DECK_CSS = `
.rd-card { display: grid; grid-template-columns: 1fr 340px; gap: 16px; align-items: start; }
@media (max-width: 860px) { .rd-card { grid-template-columns: 1fr; } }
.rd-evidence {
  background: #20272b; color: #e8eceb; border-radius: 12px; padding: 14px 16px;
  position: sticky; top: 12px; min-height: 160px;
}
.rd-evidence-ts { font-family: var(--font-mono); font-size: 12px; color: #5eead4; margin-bottom: 10px; }
.rd-evidence-quote { font-size: 13.5px; line-height: 1.55; font-style: italic; color: #d5dbd9; white-space: pre-wrap; margin: 0; }
.rd-progress { height: 4px; background: var(--kt-strip-bg); border-radius: 999px; overflow: hidden; }
.rd-progress > span { display: block; height: 100%; width: 100%; background: var(--kt-teal); border-radius: 999px; transform-origin: left center; transition: transform 0.2s ease; }
`;

export function ReviewDeck({
  customerId, visitId, items, actions, carryOver, onChanged, onClose, onExport,
}: {
  customerId: string;
  visitId: string;
  items: DiscussionItem[];
  actions: VisitAction[];
  carryOver: DiscussionItem[];
  onChanged: () => void;
  onClose: () => void;
  onExport?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const queue = useMemo<QueueCard[]>(() => {
    const qi = items.filter((i) => i.reviewState === "proposed").map((item) => ({ kind: "item" as const, item }));
    const qa = actions.filter((a) => a.reviewState === "proposed").map((action) => ({ kind: "action" as const, action }));
    return [...qi, ...qa];
  }, [items, actions]);

  const [index, setIndex] = useState(0);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [raising, setRaising] = useState<{ subject: RaiseSubject; target: RaiseTarget } | null>(null);
  const [tally, setTally] = useState({ kept: 0, dismissed: 0, raised: 0 });

  // Local editable drafts for the current item card
  const current = queue[index] ?? null;
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<ItemStatus>("open");
  const [disposition, setDisposition] = useState<ItemDisposition>("discuss");
  const [actionText, setActionText] = useState("");
  const [actionOwner, setActionOwner] = useState("");

  useEffect(() => {
    if (!current) return;
    setEditing(false);
    if (current.kind === "item") {
      setTopic(current.item.topic);
      setNotes(current.item.notes ?? "");
      setStatus(current.item.status);
      setDisposition(current.item.disposition);
    } else {
      setActionText(current.action.text);
      setActionOwner(current.action.owner ?? "");
    }
  }, [current?.kind === "item" ? current.item.id : current?.kind === "action" ? current.action.id : null]);

  const carryHintLabel = (id: number | null | undefined): string | null => {
    if (id == null) return null;
    const hit = carryOver.find((c) => c.id === id) ?? items.find((i) => i.id === id);
    return hit ? hit.topic : `item #${id}`;
  };

  const advance = useCallback((decision: "kept" | "dismissed" | "raised") => {
    setTally((t) => ({ ...t, [decision]: t[decision] + 1 }));
    setIndex((i) => i + 1);
  }, []);

  const dismiss = useCallback(async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      if (current.kind === "item") {
        await patchDiscussionItem(current.item.id, { reviewState: "dismissed" });
      } else {
        await patchVisitAction(current.action.id, { reviewState: "dismissed" });
      }
      onChanged();
      advance("dismissed");
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Couldn't dismiss — try again", { variant: "error" });
    } finally {
      setBusy(false);
    }
  }, [current, busy, onChanged, advance, enqueueSnackbar]);

  const acceptItem = useCallback(async (andRaise: boolean) => {
    if (!current || current.kind !== "item" || busy) return;
    setBusy(true);
    try {
      await patchDiscussionItem(current.item.id, {
        topic: topic.trim() || current.item.topic,
        notes: notes.trim() || null,
        status,
        disposition,
        reviewState: "accepted",
      });
      onChanged();
      if (andRaise || disposition === "raise" || disposition === "raise-fr" || disposition === "road-map-fr") {
        if (andRaise) {
          setRaising({
            subject: { kind: "item", item: { ...current.item, topic, notes: notes || null, status, disposition, reviewState: "accepted" } },
            target: RAISE_DEFAULT[disposition] ?? "support",
          });
          // Don't advance yet — wait for dialog close; still count as kept
          setTally((t) => ({ ...t, kept: t.kept + 1 }));
          setIndex((i) => i + 1);
          return;
        }
      }
      advance("kept");
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Couldn't accept — try again", { variant: "error" });
    } finally {
      setBusy(false);
    }
  }, [current, busy, topic, notes, status, disposition, onChanged, advance, enqueueSnackbar]);

  const acceptActionRaise = useCallback(() => {
    if (!current || current.kind !== "action" || busy) return;
    const target = (current.action.suggestedTarget as RaiseTarget | null) ?? "support";
    const safeTarget: RaiseTarget =
      target === "support" || target === "feature-request" || target === "dev" ? target : "support";
    setRaising({
      subject: { kind: "action", action: { ...current.action, text: actionText || current.action.text, owner: actionOwner || null } },
      target: safeTarget,
    });
  }, [current, busy, actionText, actionOwner]);

  const onRaiseDone = () => {
    setRaising(null);
    setTally((t) => ({ ...t, raised: t.raised + 1, kept: t.kept + (current?.kind === "action" ? 1 : 0) }));
    if (current?.kind === "action") setIndex((i) => i + 1);
    onChanged();
  };

  const ticketWorthy = (d: ItemDisposition) => d === "raise" || d === "raise-fr" || d === "road-map-fr";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        if (e.key === "Escape") (e.target as HTMLElement).blur();
        return;
      }
      if (raising || busy || !current) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        if (current.kind === "item") void acceptItem(false);
        else acceptActionRaise();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        void dismiss();
      } else if (e.key === "e" || e.key === "E") {
        e.preventDefault();
        setEditing(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [raising, busy, current, acceptItem, acceptActionRaise, dismiss]);

  const done = index >= queue.length;
  const progressPct = queue.length === 0 ? 100 : Math.min(100, (index / queue.length) * 100);

  // End-screen: accepted ticket-candidates still without a jira key
  const unraised = items.filter(
    (i) => i.reviewState === "accepted" && !i.jiraKey && ticketWorthy(i.disposition),
  );

  return (
    <Card style={{ borderColor: "var(--kt-teal)", boxShadow: "0 0 0 1px color-mix(in srgb, var(--kt-teal) 25%, transparent)" }}>
      <style>{DECK_CSS}</style>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Badge tone="info" icon="list-check">Review suggestions</Badge>
          <span style={{ fontSize: 13, color: "var(--kt-text-muted)", fontFamily: "var(--font-mono)" }}>
            {done ? "Done" : `${Math.min(index + 1, queue.length)} / ${queue.length}`}
          </span>
          <span style={{ fontSize: 12, color: "var(--kt-text-muted)" }}>
            kept {tally.kept} · dismissed {tally.dismissed} · raised {tally.raised}
          </span>
        </div>
        <Button size="sm" onClick={onClose}>Exit — resume later</Button>
      </div>

      <div className="rd-progress" style={{ marginBottom: 16 }}>
        <span style={{ transform: `scaleX(${progressPct / 100})` }} />
      </div>

      {done ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Grid cols="3" gap={12}>
            <MetricCard label="Kept" value={String(tally.kept)} />
            <MetricCard label="Dismissed" value={String(tally.dismissed)} />
            <MetricCard label="Raised" value={String(tally.raised)} />
          </Grid>
          {unraised.length > 0 && (
            <div>
              <div className="kt-record-label" style={{ marginBottom: 8 }}>Ticket candidates still unraised</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {unraised.map((item) => (
                  <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid var(--kt-row-divider)" }}>
                    <span style={{ flex: 1, fontSize: 13 }}>{item.topic}</span>
                    <Button
                      size="sm"
                      icon="ticket"
                      onClick={() => setRaising({
                        subject: { kind: "item", item },
                        target: RAISE_DEFAULT[item.disposition] ?? "support",
                      })}
                    >
                      Raise
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button onClick={onClose}>Back to visit</Button>
            {onExport && (
              <Button variant="primary" icon="download" onClick={onExport}>Export report</Button>
            )}
          </div>
        </div>
      ) : current && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <Pill icon={current.kind === "item" ? "messages" : "checkbox"}>
              {current.kind === "item" ? "Discussion" : "Action"}
            </Pill>
            {current.kind === "item" && current.item.carryHintItemId != null && (
              <Pill title="Possible carry-over match">
                Possible carry-over: {carryHintLabel(current.item.carryHintItemId)}
              </Pill>
            )}
          </div>

          <div className="rd-card">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {current.kind === "item" ? (
                <>
                  <TextField
                    label="Item"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    disabled={!editing && !busy ? false : busy}
                    readOnly={!editing}
                  />
                  <TextArea
                    label="Detail"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    disabled={busy}
                    readOnly={!editing}
                  />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <SelectField
                      label="Status"
                      value={status}
                      onChange={(e) => { setStatus(e.target.value as ItemStatus); setEditing(true); }}
                      disabled={busy}
                    >
                      {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </SelectField>
                    <SelectField
                      label="Ticket"
                      value={disposition}
                      onChange={(e) => { setDisposition(e.target.value as ItemDisposition); setEditing(true); }}
                      disabled={busy}
                    >
                      {TICKET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </SelectField>
                  </div>
                </>
              ) : (
                <>
                  <TextField
                    label="Action"
                    value={actionText}
                    onChange={(e) => setActionText(e.target.value)}
                    disabled={busy}
                    readOnly={!editing}
                  />
                  <TextField
                    label="Owner"
                    value={actionOwner}
                    onChange={(e) => setActionOwner(e.target.value)}
                    disabled={busy}
                    readOnly={!editing}
                  />
                  {current.action.suggestedTarget && (
                    <p style={{ fontSize: 12.5, color: "var(--kt-text-secondary)", margin: 0 }}>
                      AI suggests raising to <strong>{current.action.suggestedTarget}</strong>
                    </p>
                  )}
                  <p style={{ fontSize: 12.5, color: "var(--kt-text-muted)", margin: 0 }}>
                    Accepting an action creates or links a Jira issue — Jira is the source of truth.
                  </p>
                </>
              )}
            </div>

            <div className="rd-evidence">
              <div className="rd-evidence-ts">
                {current.kind === "item"
                  ? (current.item.evidenceTs ?? "—")
                  : (current.action.evidenceTs ?? "—")}
              </div>
              <p className="rd-evidence-quote">
                {(current.kind === "item" ? current.item.evidence : current.action.evidence)
                  ?? "No transcript excerpt attached to this suggestion."}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            <Button icon="arrow-left" disabled={busy} onClick={() => void dismiss()}>Dismiss</Button>
            <Button icon="pencil" disabled={busy} onClick={() => setEditing(true)}>Edit</Button>
            <div style={{ flex: 1 }} />
            {current.kind === "item" && ticketWorthy(disposition) && (
              <Button disabled={busy} icon="ticket" onClick={() => void acceptItem(true)}>
                Accept + raise
              </Button>
            )}
            {current.kind === "item" ? (
              <Button variant="primary" icon="arrow-right" disabled={busy} onClick={() => void acceptItem(false)}>
                Accept
              </Button>
            ) : (
              <>
                <Button
                  disabled={busy}
                  icon="link"
                  onClick={() => {
                    setRaising({
                      subject: { kind: "action", action: current.action },
                      target: "support",
                    });
                  }}
                >
                  Link existing…
                </Button>
                <Button variant="primary" icon="ticket" disabled={busy} onClick={acceptActionRaise}>
                  Accept → create Jira
                </Button>
              </>
            )}
          </div>
          <p style={{ fontSize: 11, color: "var(--kt-text-muted)", margin: "10px 0 0" }}>
            Keyboard: → accept · ← dismiss · e edit
          </p>
        </>
      )}

      {raising && (
        <RaiseTicketDialog
          key={raising.subject.kind === "item" ? raising.subject.item.id : raising.subject.action.id}
          customerId={customerId}
          visitId={visitId}
          subject={raising.subject}
          defaultTarget={raising.target}
          onClose={() => {
            // If user cancels action raise without linking, stay on card; for items we already advanced.
            setRaising(null);
            if (current?.kind === "action") { /* stay */ }
            else onChanged();
          }}
          onChanged={onRaiseDone}
        />
      )}
    </Card>
  );
}
